import { z } from 'zod'

// All possible account statuses (includes system-only 'invited')
export const allCustomerStatusSchema = z.enum(['invited', 'active', 'credit_hold', 'suspended', 'inactive'])
export type AllCustomerStatus = z.infer<typeof allCustomerStatusSchema>

// Statuses admin can manually set (never 'invited' — system-controlled only)
export const customerStatusSchema = z.enum(['active', 'credit_hold', 'suspended', 'inactive'])
export type CustomerStatus = z.infer<typeof customerStatusSchema>

// Account status enum for CSV import (only active statuses make sense on import)
const accountStatusSchema = z.enum(['active', 'credit_hold', 'suspended'])

// --------------------------------------------------------------------------
// profileSchema — personal info fields only
// Used directly by: Admin AccountPage profile edit
// --------------------------------------------------------------------------
export const profileSchema = z.object({
  first_name: z
    .string()
    .min(1, 'First name is required')
    .max(100, 'First name must be 100 characters or less')
    .transform((val) => val.trim()),
  last_name: z
    .string()
    .min(1, 'Last name is required')
    .max(100, 'Last name must be 100 characters or less')
    .transform((val) => val.trim()),
  company: z
    .string()
    .max(200, 'Company must be 200 characters or less')
    .optional()
    .transform((val) => val?.trim() || undefined),
  phone_number: z
    .string()
    .max(20, 'Phone number must be 20 characters or less')
    .optional()
    .transform((val) => val?.trim() || undefined),
})

export type ProfileFormInput = z.input<typeof profileSchema>
export type ProfileFormData = z.infer<typeof profileSchema>

// --------------------------------------------------------------------------
// customerProfileSchema — personal info + optional billing address
// Used by: Customer AccountPage profile edit
// --------------------------------------------------------------------------
export const customerProfileSchema = profileSchema.extend({
  billing_street: z
    .string()
    .max(200, 'Street address must be 200 characters or less')
    .optional()
    .transform((val) => val?.trim() || undefined),
  billing_street2: z
    .string()
    .optional()
    .transform((val) => val?.trim() || undefined),
  billing_city: z
    .string()
    .max(100, 'City must be 100 characters or less')
    .optional()
    .transform((val) => val?.trim() || undefined),
  billing_state: z
    .string()
    .max(50, 'State must be 50 characters or less')
    .optional()
    .transform((val) => val?.trim() || undefined),
  billing_zip: z
    .string()
    .max(20, 'ZIP code must be 20 characters or less')
    .optional()
    .transform((val) => val?.trim() || undefined),
  billing_country: z
    .string()
    .optional()
    .default('USA')
    .transform((val) => val?.trim() || 'USA'),
})

export type CustomerProfileFormInput = z.input<typeof customerProfileSchema>
export type CustomerProfileFormData = z.infer<typeof customerProfileSchema>

// --------------------------------------------------------------------------
// shippingAddressSchema — for PlaceOrderModal when not shipping to billing
// --------------------------------------------------------------------------
export const shippingAddressSchema = z.object({
  street: z.string().min(1, 'Street address is required').transform((val) => val.trim()),
  street2: z.string().optional().transform((val) => val?.trim() || undefined),
  city: z.string().min(1, 'Please select a valid address, city is missing').transform((val) => val.trim()),
  state: z.string().min(1, 'Please select a valid address, state is missing').transform((val) => val.trim()),
  zip: z.string().min(1, 'Please select a valid address, ZIP is missing').transform((val) => val.trim()),
})

export type ShippingAddressData = z.infer<typeof shippingAddressSchema>

// --------------------------------------------------------------------------
// customerSchema — full onboarding schema (required billing address)
// Used by: CustomerOnboardingModal
// --------------------------------------------------------------------------
export const customerSchema = customerProfileSchema.extend({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  // Override billing fields to be required for onboarding
  billing_street: z
    .string()
    .min(1, 'Street address is required')
    .transform((val) => val.trim()),
  billing_city: z
    .string()
    .min(1, 'City is required')
    .transform((val) => val.trim()),
  billing_state: z
    .string()
    .min(1, 'State is required')
    .transform((val) => val.trim()),
  billing_zip: z
    .string()
    .min(1, 'ZIP code is required')
    .transform((val) => val.trim()),
  // Business fields
  account_status: accountStatusSchema.optional().default('active'),
  customer_notes: z.string().max(2000).optional().transform((val) => val?.trim() || undefined),
  require_po_number: z.boolean().optional().default(false),
  shortage_preference: z.enum(['backorder', 'ship_complete', 'cancel']).optional().default('backorder'),
  payment_terms: z.enum(['net30', 'net60', 'net90', 'cod', 'prepaid', 'credit_card']).optional().nullable(),
  credit_limit: z
    .union([z.string(), z.number()])
    .optional()
    .default(0)
    .transform((val) => {
      if (val === '' || val === undefined || val === null) return 0
      const num = typeof val === 'number' ? val : parseFloat(String(val))
      return isNaN(num) ? 0 : num
    })
    .pipe(
      z.number()
        .min(0, 'Credit limit cannot be negative')
        .max(999_999.99, 'Credit limit cannot exceed $999,999.99')
    ),
})

export type CustomerFormInput = z.input<typeof customerSchema>
export type CustomerFormData = z.infer<typeof customerSchema>

// Schema for updating customer (admin-settable statuses only — 'invited' is system-controlled)
export const customerUpdateSchema = customerSchema.omit({ account_status: true }).extend({
  status: customerStatusSchema.default('active'),
})

export type CustomerUpdateFormInput = z.input<typeof customerUpdateSchema>
export type CustomerUpdateFormData = z.infer<typeof customerUpdateSchema>

// Schema for bulk customer import (CSV)
export const bulkCustomerSchema = z.array(
  z.object({
    email: z.string().email('Invalid email'),
    password: z.string().min(8, 'Password must be at least 8 characters').optional(),
    first_name: z.string().optional().transform((val) => val?.trim() || undefined),
    last_name: z.string().optional().transform((val) => val?.trim() || undefined),
    company: z.string().optional().transform((val) => val?.trim() || undefined),
    phone_number: z.string().optional().transform((val) => val?.trim() || undefined),
    billing_street: z.string().optional().transform((val) => val?.trim() || undefined),
    billing_street2: z.string().optional().transform((val) => val?.trim() || undefined),
    billing_city: z.string().optional().transform((val) => val?.trim() || undefined),
    billing_state: z.string().optional().transform((val) => val?.trim() || undefined),
    billing_zip: z.string().optional().transform((val) => val?.trim() || undefined),
    billing_country: z.string().optional().transform((val) => val?.trim() || undefined),
    account_status: accountStatusSchema.optional(),
    credit_limit: z.union([z.string(), z.number()])
      .optional()
      .default(0)
      .transform((val) => {
        if (val === '' || val === undefined || val === null) return 0
        const num = typeof val === 'number' ? val : parseFloat(String(val))
        return isNaN(num) ? 0 : num
      })
      .pipe(
        z.number()
          .min(0, 'Credit limit cannot be negative')
          .max(999_999.99, 'Credit limit cannot exceed $999,999.99')
      ),
  })
).min(1, 'At least one customer is required').max(500, 'Maximum 500 customers per import')

export type BulkCustomerFormData = z.infer<typeof bulkCustomerSchema>
